import React, { useMemo, useRef } from 'react';
import type { AdditionalVehicleDetailsFormInputs } from '../../schemas/additionalvehicledetails.schema';
import { Form, Button } from '@/shared/components';
import type { Vehicle } from '../../types/vinvrmsearch.types';
import type { VehicleDerivative } from '../../types/manualsearch.types';
import { deriveIssues, type SeededVehicleDetails } from './additionalVehicleDetailsFormState';
import { useAdditionalVehicleDetailsFormState } from '../../hooks/useAdditionalVehicleDetailsFormState';
import { useRegistrationDateBlockedSignal } from '../../hooks/useRegistrationDateBlockedSignal';
import { useVehicleDetailsAutoSubmit } from '../../hooks/useVehicleDetailsAutoSubmit';
import { AutoSaveStatusBanner } from './AutoSaveStatusBanner';
import { VehicleIdentifierFields } from './VehicleIdentifierFields';
import { MileageFields } from './MileageFields';
import { RegistrationDateField } from './RegistrationDateField';
import { ServiceHistoryDateFields } from './ServiceHistoryDateFields';

interface AdditionalVehicleDetailsFormProps {
    onSubmit: (data: AdditionalVehicleDetailsFormInputs) => void;
    selectedVehicle?: Vehicle | VehicleDerivative;
    autoSubmit?: boolean;
    seededDetails?: SeededVehicleDetails | null;
    isMotLoading?: boolean;
    /** Starts the mileage inputs at the MOT estimate rather than empty. */
    prePopulateMileage?: boolean;
    // Reports whether registrationDate, the one field the step gate needs, is blocking; deliberately not whole-form isValid,
    // which seeded out-of-bounds vin/vrm hold false silently. Derived from deriveIssues' issue codes, not message presence.
    onRegistrationDateBlockedChange?: (blocked: boolean) => void;
}

export const AdditionalVehicleDetailsForm: React.FC<AdditionalVehicleDetailsFormProps> = ({
    onSubmit,
    selectedVehicle,
    autoSubmit = true,
    seededDetails,
    isMotLoading = false,
    prePopulateMileage = false,
    onRegistrationDateBlockedChange,
}) => {
    const estimate = seededDetails?.current_estimated_mileage;
    const annualEstimate = seededDetails?.average_annual_mileage;

    // Bridges useVehicleDetailsAutoSubmit's clearLastSubmitted into the reset
    // effect owned by useAdditionalVehicleDetailsFormState, called first below.
    const clearLastSubmittedRef = useRef<(() => void) | null>(null);

    const {
        control,
        handleSubmit,
        watchedValues,
        isValid,
        lastServiceMileageEstimate,
        onLastServiceDatePicked,
    } = useAdditionalVehicleDetailsFormState({
        selectedVehicle,
        seededDetails,
        prePopulateMileage,
        clearLastSubmittedRef,
    });

    // Re-derives issues by parsing watch() directly: RHF's mount/reset validity pass sets isValid without populating errors,
    // and trigger() would commit errors at rest. watch() is fresh each render so this memo caches nothing, by design.
    const { fieldIssues, formIssues, registrationDateBlocked } = useMemo(
        () => deriveIssues(watchedValues),
        [watchedValues],
    );

    useRegistrationDateBlockedSignal(registrationDateBlocked, onRegistrationDateBlockedChange);

    const { isSubmitting, handleFormSubmit, clearLastSubmitted } = useVehicleDetailsAutoSubmit({
        autoSubmit,
        isValid,
        watchedValues,
        onSubmit,
    });
    clearLastSubmittedRef.current = clearLastSubmitted;

    return (
        <Form
            onSubmit={handleSubmit(handleFormSubmit)}
            className="rounded-lg border border-mp-border bg-mp-surface"
        >
            <div className="px-6 py-4 space-y-4">
                <AutoSaveStatusBanner autoSubmit={autoSubmit} fieldIssues={fieldIssues} formIssues={formIssues} />
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <VehicleIdentifierFields control={control} watchedValues={watchedValues} fieldIssues={fieldIssues} />
                    <MileageFields
                        control={control}
                        watchedValues={watchedValues}
                        fieldIssues={fieldIssues}
                        estimate={estimate}
                        annualEstimate={annualEstimate}
                        isMotLoading={isMotLoading}
                    />
                    <RegistrationDateField control={control} fieldIssues={fieldIssues} />
                    <ServiceHistoryDateFields
                        control={control}
                        watchedValues={watchedValues}
                        fieldIssues={fieldIssues}
                        isMotLoading={isMotLoading}
                        lastServiceMileageEstimate={lastServiceMileageEstimate}
                        onLastServiceDatePicked={onLastServiceDatePicked}
                    />
                </div>
                {!autoSubmit && (
                    <Button disabled={isSubmitting} className="w-full mt-8">
                        {isSubmitting ? 'Saving Vehicle Details...' : 'Save Vehicle Details'}
                    </Button>
                )}
            </div>
        </Form>
    );
};
